import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Links from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import Swal from "sweetalert2";
import { FilterMatchMode } from "primereact/api";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import { Modal, Button } from "react-bootstrap";
import FeaturePicker from "./components/FeaturePicker";
import OrgActivityDrawer from "./components/OrgActivityDrawer";
import LoginHeader from "./components/LoginHeader.js";
import AADHeader from "./components/AADHeader.js";
import SideNavigation from "./components/SideNavigation.js";
import DeleteConfirmation from "./components/DeleteConfirmation";
import PermissionTooltip from "./components/PermissionTooltip";
import NoAccess from "./components/NoAccess";
import { toTreeNodes, comingSoonLabels, selectionFromKeys, keysFromSelection, describeFeatures, formatPlanDate } from "./utils/features";
import "./table.css";

const PLANS = [
  { label: "Trial", value: "trial" },
  { label: "Standard", value: "standard" },
];
const isoDate = (v) => (v ? String(v).slice(0, 10) : "");
const fmtDate = (v) => formatPlanDate(v) || "—";
const serverMessage = (data, fallback) =>
  (data && (data.error || (typeof data.data === "string" && data.data) || data.message)) || fallback;
const problemsHtml = (data) =>
  Array.isArray(data && data.problems) && data.problems.length
    ? `<ul style="text-align:left">${data.problems.map((p) => `<li>${escapeHtml(p)}</li>`).join("")}</ul>`
    : null;

// Staff-only overview of every organization on the platform: provision,
// deprovision, edit plan / trial end / feature set, and manage EcoPrism staff.
// Backed by the proxy's /admin/* routes (requireStaff on the server; the
// PLATFORM_STAFF route guard in main.js is only UX).
export default function Organizations() {
  const username = localStorage.getItem("userName");
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [loading, setLoading] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [denied, setDenied] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [target, setTarget] = useState(null); // org pending deprovision
  const [deprovisioning, setDeprovisioning] = useState(false);
  const [activityOrg, setActivityOrg] = useState(null);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    loginType: { value: null, matchMode: FilterMatchMode.CONTAINS },
    planText: { value: null, matchMode: FilterMatchMode.CONTAINS },
    adminsText: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [showExpiringOnly, setShowExpiringOnly] = useState(false);

  // Edit-plan modal state
  const [editing, setEditing] = useState(null); // org row
  const [editPlan, setEditPlan] = useState("trial");
  const [editEnds, setEditEnds] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [editSelection, setEditSelection] = useState({});
  const [saving, setSaving] = useState(false);

  // Staff modal state
  const [staffOpen, setStaffOpen] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [staffEmail, setStaffEmail] = useState("");
  const [staffBusy, setStaffBusy] = useState(false);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  useEffect(() => {
    const handleResize = () => setIsSidebarOpen(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const headers = { "Content-type": "application/json", username };
  const treeNodes = toTreeNodes(catalogue);

  // Flatten the backend summary into display fields once so the table's
  // filter/sort work on plain strings.
  const toRow = (o, tree) => {
    const isEntra = o.authenticationType === "AAD";
    const plan = (o.plan || "standard").toLowerCase();
    const ends = o.planEndsAt || null;
    // A trial runs through the end of its last day (UTC), matching the proxy.
    const msLeft = ends ? new Date(ends).getTime() + 24 * 60 * 60 * 1000 - 1 - Date.now() : null;
    const expired = plan === "trial" && msLeft !== null && msLeft < 0;
    const daysLeft = plan === "trial" && msLeft !== null && msLeft >= 0 ? Math.ceil(msLeft / (24 * 60 * 60 * 1000)) : null;
    const expiringSoon = plan === "trial" && !expired && daysLeft !== null && daysLeft <= 7;
    return {
      ...o,
      id: o.entityId,
      plan,
      planEndsAt: ends,
      expired,
      daysLeft,
      expiringSoon,
      loginType: isEntra ? "Microsoft Entra" : o.authenticationType ? "Password" : "—",
      planText: plan === "trial" ? (expired ? "Trial (ended)" : expiringSoon ? `Trial (${daysLeft}d left)` : "Trial") : "Standard",
      tenantText: [...(o.tenants || []), ...(o.authenticatedDomains || [])].join(", ") || "—",
      featureLabels: describeFeatures(o.enabledFeatures || [], tree),
      adminsText: (o.adminEmails || []).join(", ") || "—",
    };
  };

  async function fetchCatalogue() {
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}features`);
      const data = await res.json();
      const tree = Array.isArray(data) ? data : (data && data.data) || [];
      setCatalogue(tree);
      return tree;
    } catch (err) {
      console.error(err);
      return [];
    }
  }

  async function fetchOrgs(tree = catalogue) {
    setLoading(true);
    setLoadError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}admin/orgs`, { headers });
      if (res.status === 401 || res.status === 403) {
        setDenied(true);
        return;
      }
      const data = await res.json();
      if (res.status === 200 && data && data.success !== false && Array.isArray(data.data)) {
        setOrgs(data.data.map((o) => toRow(o, tree)));
      } else {
        setLoadError(serverMessage(data, "Could not load organizations."));
      }
    } catch (err) {
      console.error(err);
      setLoadError("Unable to reach the server. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async () => {
      const tree = await fetchCatalogue();
      await fetchOrgs(tree);
    })();
    // Surface the "just provisioned" message the form hands over via router state.
    const flash = location.state && location.state.flash;
    if (flash) {
      Swal.fire({
        icon: flash.icon || "success",
        html: flash.html,
        iconHtml: flash.icon === "success" || !flash.icon
          ? '<img src="assets/img/successicon.png" style="width: 55px; height: 55px;" />'
          : undefined,
      });
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── deprovision ────────────────────────────────────────────────────────────
  async function deprovision(org) {
    if (!org) return;
    setDeprovisioning(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}admin/orgs/deprovision`, {
        method: "POST",
        headers,
        body: JSON.stringify({ companyId: org.entityId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 200 && data && data.success !== false) {
        setTarget(null);
        await fetchOrgs();
        const ids = Array.isArray(data.identities) ? data.identities : [];
        const idText = ids.length
          ? ids.map((i) => `${escapeHtml(i.email)}: ${i.ok && i.deleted !== false ? "sign-in identity removed" : i.ok ? "no identity to remove" : `could not remove (${escapeHtml(i.error || "unknown")})`}`).join("<br/>")
          : "";
        Swal.fire({
          icon: "success",
          title: `${org.name} deprovisioned`,
          html: `<p>Its users can no longer sign in.</p>${idText ? `<p style="font-size:13px">${idText}</p>` : ""}`,
        });
      } else {
        Swal.fire({ icon: "error", title: "Could not deprovision", text: serverMessage(data, "Please try again.") });
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", title: "Could not deprovision", text: "Unable to reach the server. Please try again." });
    } finally {
      setDeprovisioning(false);
    }
  }

  // ── edit plan ──────────────────────────────────────────────────────────────
  const openEdit = (row) => {
    setEditing(row);
    setEditPlan(row.plan || "standard");
    setEditEnds(isoDate(row.planEndsAt));
    setEditActive(row.isActive !== false);
    setEditSelection(selectionFromKeys(treeNodes, row.enabledFeatures || []));
  };
  const closeEdit = () => { if (!saving) setEditing(null); };

  // Shared plan-update call for the edit modal and Convert to Standard.
  // Returns true on success (caller decides what to close / say).
  async function submitPlan(entityId, body, successTitle, successText) {
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}admin/orgs/${encodeURIComponent(entityId)}/plan`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 200 && data && data.success !== false) {
        await fetchOrgs();
        Swal.fire({ icon: "success", title: successTitle, text: successText });
        return true;
      }
      Swal.fire({ icon: "error", title: "Could not update plan", html: problemsHtml(data) || escapeHtml(serverMessage(data, "Please try again.")) });
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", title: "Could not update plan", text: "Unable to reach the server. Please try again." });
    }
    return false;
  }

  async function savePlan() {
    if (!editing) return;
    const enabledFeatures = keysFromSelection(treeNodes, editSelection);
    if (!enabledFeatures.length) {
      Swal.fire({ icon: "error", title: "Pick at least one module" });
      return;
    }
    if (editPlan === "trial" && !editEnds) {
      Swal.fire({ icon: "error", title: "A trial needs an end date" });
      return;
    }
    setSaving(true);
    const body = { plan: editPlan, enabledFeatures, isActive: editActive };
    if (editPlan === "trial") body.planEndsAt = editEnds;
    const ok = await submitPlan(editing.entityId, body, "Plan updated",
      `${editing.name}: ${editPlan}${editPlan === "trial" ? ` until ${fmtDate(editEnds)}` : ""}.`);
    setSaving(false);
    if (ok) setEditing(null);
  }

  async function convertToStandard(row) {
    const confirm = await Swal.fire({
      icon: "question",
      title: `Convert ${row.name} to Standard?`,
      text: "The trial end date is removed and every enabled module stays on permanently.",
      showCancelButton: true,
      confirmButtonText: "Convert",
      confirmButtonColor: "#407775",
    });
    if (!confirm.isConfirmed) return;
    await submitPlan(row.entityId, { plan: "standard" }, "Converted to Standard", `${row.name} is now on the standard plan.`);
  }

  // ── staff ──────────────────────────────────────────────────────────────────
  async function loadStaff() {
    setStaffBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}admin/staff`, { headers });
      const data = await res.json().catch(() => ({}));
      setStaffList(res.status === 200 && Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setStaffBusy(false);
    }
  }
  const openStaff = () => { setStaffOpen(true); setStaffEmail(""); loadStaff(); };

  async function addStaff(e) {
    e.preventDefault();
    const email = staffEmail.trim();
    if (!email) return;
    setStaffBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}admin/staff`, { method: "POST", headers, body: JSON.stringify({ email }) });
      const data = await res.json().catch(() => ({}));
      if (res.status === 200 && data && data.success !== false) {
        setStaffEmail("");
        await loadStaff();
      } else {
        Swal.fire({ icon: "error", title: "Could not add", text: serverMessage(data, "Please try again.") });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not add", text: "Unable to reach the server." });
    } finally {
      setStaffBusy(false);
    }
  }

  async function removeStaff(email) {
    const ok = await Swal.fire({
      icon: "warning",
      title: `Remove ${email} from staff?`,
      text: "They lose access to this page immediately.",
      showCancelButton: true,
      confirmButtonText: "Remove",
      confirmButtonColor: "#CC2229",
    });
    if (!ok.isConfirmed) return;
    setStaffBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}admin/staff/${encodeURIComponent(email)}`, { method: "DELETE", headers });
      const data = await res.json().catch(() => ({}));
      if (res.status === 200 && data && data.success !== false) {
        await loadStaff();
      } else {
        Swal.fire({ icon: "error", title: "Could not remove", text: serverMessage(data, "Please try again.") });
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not remove", text: "Unable to reach the server." });
    } finally {
      setStaffBusy(false);
    }
  }

  // ── render ─────────────────────────────────────────────────────────────────
  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setFilters((f) => ({ ...f, global: { ...f.global, value } }));
    setGlobalFilterValue(value);
  };

  const renderHeader = () => {
    const loginType = localStorage.getItem("loginType");
    if (loginType === "AAD") {
      return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
    }
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
  };

  if (denied) {
    return <NoAccess message="Only EcoPrism staff can manage organizations." />;
  }

  const expiringCount = orgs.filter((o) => o.expiringSoon).length;
  const visibleOrgs = showExpiringOnly ? orgs.filter((o) => o.expiringSoon) : orgs;
  const tableHeader = (
    <div className="flex justify-content-end">
      <div className="col-md-6 pd-0" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span className="record-count">{visibleOrgs.length}</span>
        <span className="record-title">Organizations</span>
        {expiringCount > 0 && (
          <button
            type="button"
            className={`btn btn-sm ${showExpiringOnly ? "btn-warning" : "btn-outline-warning"}`}
            onClick={() => setShowExpiringOnly((v) => !v)}
            style={{ borderRadius: 8, whiteSpace: "nowrap" }}
            title="Show only trials ending within 7 days"
          >
            {expiringCount} expiring soon
          </button>
        )}
      </div>
      <div className="col-md-6 pd-0 mt-top10 txt-end">
        <span className="p-input-icon-left">
          <img src="assets/img/search.png" className="search-i" alt="" />
          <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Search" />
        </span>
      </div>
    </div>
  );

  const planBody = (row) => (
    <span>
      <Tag
        value={row.planText}
        severity={row.plan === "trial" ? (row.expired ? "danger" : row.expiringSoon ? "warning" : "info") : "success"}
        rounded
      />
    </span>
  );
  const endsBody = (row) => (
    <span style={{ color: row.expired ? "#CC2229" : undefined, whiteSpace: "nowrap" }}>
      {row.plan === "trial" ? fmtDate(row.planEndsAt) : "—"}
    </span>
  );
  const featuresBody = (row) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {row.featureLabels.map((f) => (
        <Tag key={f} value={f} style={{ background: "#e3efed", color: "#2f6b66", fontWeight: 500 }} />
      ))}
    </div>
  );
  const actionsBody = (row) => (
    <div style={{ display: "flex", gap: 6 }}>
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => openEdit(row)} style={{ whiteSpace: "nowrap", borderRadius: "8px" }}>
        Edit plan
      </button>
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setActivityOrg(row)} style={{ whiteSpace: "nowrap", borderRadius: "8px" }}>
        Activity
      </button>
      {row.plan === "trial" && (
        <button type="button" className="btn btn-sm btn-outline-success" onClick={() => convertToStandard(row)} style={{ whiteSpace: "nowrap", borderRadius: "8px" }}>
          Convert to Standard
        </button>
      )}
      <PermissionTooltip disabled={!row.isActive} reason="This organization is already inactive">
        <button
          type="button"
          className="btn btn-sm btn-outline-danger"
          onClick={row.isActive ? () => setTarget(row) : undefined}
          disabled={!row.isActive}
          style={{ whiteSpace: "nowrap", borderRadius: "8px" }}
        >
          Deprovision
        </button>
      </PermissionTooltip>
    </div>
  );

  return (
    <div id="wrapper">
      {loading && (
        <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div className="container formpd">
          <div className="flex">
            <div className="col-md-8">
              <div className="steps__containt-hader">
                <h3 className="title">Organizations</h3>
                <div role="presentation">
                  <Breadcrumbs aria-label="breadcrumb" separator={<NavigateNextIcon fontSize="small" />}>
                    <Links underline="hover" color="inherit">Settings</Links>
                    <Typography color="text.primary">Organizations</Typography>
                  </Breadcrumbs>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="steps__containt-hader btalignleft" style={{ display: "flex", gap: 12, justifyContent: "flex-end", alignItems: "center" }}>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={openStaff} style={{ borderRadius: "8px", whiteSpace: "nowrap" }}>
                  Manage staff
                </button>
                <Link to="/organizations/new" className="add-cta btalignleft">
                  + Provision organization
                </Link>
              </div>
            </div>
          </div>
          {loadError && (
            <div className="alert alert-danger" role="alert">
              {loadError}{" "}
              <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => fetchOrgs()}>Retry</button>
            </div>
          )}
          <div className="col-md-12">
            <DataTable
              value={visibleOrgs}
              paginator
              rows={10}
              rowsPerPageOptions={[10, 20, 25, 50]}
              tableStyle={{ minWidth: "60rem" }}
              paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
              currentPageReportTemplate="{first} to {last} of {totalRecords}"
              dataKey="id"
              filters={filters}
              filterDisplay="row"
              globalFilterFields={["name", "loginType", "planText", "tenantText", "adminsText"]}
              header={tableHeader}
              emptyMessage={loading ? "Loading…" : "No organizations yet."}
            >
              <Column header="Actions" body={actionsBody} style={{ minWidth: "13rem", paddingLeft: "30px" }} />
              <Column sortable field="name" header="Organization" filter filterPlaceholder="Filter" style={{ minWidth: "10rem" }} />
              <Column sortable field="planText" header="Plan" filter filterPlaceholder="Filter" body={planBody} style={{ minWidth: "7rem" }} />
              <Column sortable field="planEndsAt" header="Ends" body={endsBody} style={{ minWidth: "7rem" }} />
              <Column
                sortable
                field="loginType"
                header="Login type"
                filter
                filterPlaceholder="Filter"
                body={(row) => (
                  <Tag value={row.loginType} severity={row.loginType === "Microsoft Entra" ? "info" : row.loginType === "Password" ? "warning" : null} rounded />
                )}
                style={{ minWidth: "8rem" }}
              />
              <Column header="Modules" body={featuresBody} style={{ minWidth: "14rem" }} />
              <Column field="tenantText" header="Tenant / domains" style={{ minWidth: "12rem", wordBreak: "break-all" }} />
              <Column sortable field="adminsText" header="Admins" filter filterPlaceholder="Filter" style={{ minWidth: "12rem", wordBreak: "break-all" }} />
              <Column field="isActive" header="Active" body={(row) => (row.isActive ? "Yes" : "No")} style={{ minWidth: "4rem", paddingRight: "30px" }} />
            </DataTable>
          </div>
        </div>
      </div>

      <DeleteConfirmation
        showModal={!!target}
        hideModal={() => !deprovisioning && setTarget(null)}
        confirmModal={() => deprovision(target)}
        deletename={target && target.entityId}
        title="Deprovision organization"
        actionLabel="Deprovision"
        busy={deprovisioning}
        message={
          target
            ? `Remove ${target.name}, all its legal entities, users and configuration? ` +
              `Its people will no longer be able to sign in` +
              (target.loginType === "Password" ? ", and their password sign-in identities will be removed" : "") +
              `. This cannot be undone.`
            : ""
        }
      />

      <OrgActivityDrawer org={activityOrg} onClose={() => setActivityOrg(null)} />

      <Modal show={!!editing} onHide={closeEdit} className="custom-modal-width-config site-modal">
        <Modal.Header>
          <div className="steps__containt-hader edit-form">
            <h3 className="title">Edit plan — {editing && editing.name}</h3>
          </div>
        </Modal.Header>
        <Modal.Body>
          <form className="edit-form" onSubmit={(e) => { e.preventDefault(); savePlan(); }}>
            <div className="form-row">
              <div className="form-group col-md-6">
                <label className="formlable" htmlFor="editPlan">Plan</label>
                <Dropdown id="editPlan" className="form-control wd-100" value={editPlan} options={PLANS} onChange={(e) => setEditPlan(e.value)} />
              </div>
              <div className="form-group col-md-6">
                <label className="formlable" htmlFor="editEnds">Trial ends</label>
                <input id="editEnds" type="date" className="form-control" value={editEnds} disabled={editPlan !== "trial"}
                  onChange={(e) => setEditEnds(e.target.value)} />
                <small className="form-text text-muted">
                  {editPlan === "trial" ? "The trial runs through this day; afterwards the organization can view but not change data." : "Standard plans don't expire."}
                </small>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group col-md-12">
                <label className="formlable">Modules</label>
                <FeaturePicker tree={treeNodes} selection={editSelection} onChange={setEditSelection} disabled={saving} comingSoon={comingSoonLabels(catalogue)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group col-md-12">
                <label className="formlable" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" checked={editActive} onChange={(e) => setEditActive(e.target.checked)} />
                  Active (unticking blocks every sign-in for this organization)
                </label>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <div className="edit-form modal-buttons">
            <Button className="btn btn-primary" disabled={saving} onClick={savePlan}>
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button className="btn btn-cancel" onClick={closeEdit} disabled={saving}>Cancel</Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={staffOpen} onHide={() => !staffBusy && setStaffOpen(false)} className="custom-modal-width-config site-modal">
        <Modal.Header>
          <div className="steps__containt-hader edit-form">
            <h3 className="title">EcoPrism staff</h3>
          </div>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 13, color: "#5b6a65" }}>
            Staff can provision and deprovision organizations and manage this list. Only EcoPrism or Acumant addresses can be added.
          </p>
          <form className="edit-form" onSubmit={addStaff} style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <input type="email" className="form-control" placeholder="name@ecoprism.com" value={staffEmail}
              onChange={(e) => setStaffEmail(e.target.value)} required />
            <Button type="submit" className="btn btn-primary" disabled={staffBusy || !staffEmail.trim()} style={{ whiteSpace: "nowrap" }}>Add</Button>
          </form>
          <table className="table custom-table" style={{ marginBottom: 0 }}>
            <tbody>
              {staffList.map((email) => (
                <tr key={email}>
                  <td>{email}</td>
                  <td style={{ textAlign: "right", width: 120 }}>
                    <button type="button" className="btn btn-sm btn-outline-danger" disabled={staffBusy || email.toLowerCase() === String(username || "").toLowerCase()}
                      onClick={() => removeStaff(email)} style={{ borderRadius: "8px" }}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
              {!staffList.length && (
                <tr><td colSpan={2} style={{ color: "#5b6a65" }}>{staffBusy ? "Loading…" : "No staff listed."}</td></tr>
              )}
            </tbody>
          </table>
        </Modal.Body>
        <Modal.Footer>
          <div className="edit-form modal-buttons">
            <Button className="btn btn-cancel" onClick={() => setStaffOpen(false)} disabled={staffBusy}>Close</Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
